import type {
  ContractSourceMeta,
  FormFolderContract,
  FormUiContract,
} from '@/contracts/questionnaire'
import type { QuestionnaireSnapshotPreviewContract } from '@/contracts/questionnaire-snapshot'
import type { QuestionType } from '@/types/form'

export type FormManagementTabId =
  | 'analytics'
  | 'export'
  | 'crossAnalysis'
  | 'visitorActivity'
  | 'linkGenerator'
  | 'notifications'
  | 'completionPage'
  | 'customerWorkflow'
  | 'operations'

export type FormManagementActionId =
  | 'edit'
  | 'open'
  | 'copyLink'
  | 'preview'
  | 'manage'
  | 'analytics'
  | 'export'
  | 'linkGenerator'
  | 'duplicate'
  | 'move'
  | 'createChild'
  | 'trash'

export type FormManagementActionGroup = 'content' | 'management' | 'operations' | 'danger'

export type FormManagementActionContract = {
  id: FormManagementActionId
  label: string
  icon: string
  group: FormManagementActionGroup
  enabled: boolean
  disabledReason?: string
}

export type FormManagementCapabilities = {
  canEdit: boolean
  canShare: boolean
  canExport: boolean
  canManageOperations: boolean
  canViewAnalytics: boolean
  canManageCustomerWorkflow: boolean
}

export type FormManagementWorkspaceContract = {
  form: FormUiContract
  folders: FormFolderContract[]
  capabilities: FormManagementCapabilities
  defaultTab: FormManagementTabId
  visibleTabs: FormManagementTabId[]
  sourceMeta: ContractSourceMeta
}

export type FormManagementPanelState =
  | 'ready'
  | 'loading'
  | 'empty'
  | 'error'
  | 'provider_not_configured'

export type FormManagementMetricTone = 'sky' | 'emerald' | 'indigo' | 'violet' | 'amber'

export type FormManagementMetricContract = {
  id: string
  label: string
  value: string
  helper: string
  trendLabel: string
  tone: FormManagementMetricTone
}

export type FormManagementTrendPoint = {
  label: string
  views: number
  starts: number
  completions: number
}

export type FormManagementDropoffRow = {
  id: string
  question: string
  reached: number
  dropoffs: number
  dropoffRate: number
}

export type FormManagementQuestionStat = {
  id: string
  question: string
  typeLabel: string
  responses: number
  topResult: string
  completionRate: number
}

export type FormManagementQuestionChartKind =
  | 'donut'
  | 'horizontal_bar'
  | 'stacked_bar'
  | 'histogram'
  | 'numeric_summary'
  | 'timeline'
  | 'text_cloud'
  | 'sample_list'
  | 'ranking'
  | 'matrix'
  | 'file_summary'
  | 'layout_block'

export type FormManagementQuestionChartSegment = {
  id: string
  label: string
  value: number
  percent: number
  color?: string
  helper?: string
}

export type FormManagementQuestionChartStack = {
  id: string
  label: string
  total: number
  segments: FormManagementQuestionChartSegment[]
}

export type FormManagementQuestionChartToken = {
  id: string
  label: string
  value: number
  weight: number
}

export type FormManagementQuestionChartSample = {
  id: string
  label: string
  value: string
  helper?: string
}

export type FormManagementQuestionChartNumericSummary = {
  min: number
  max: number
  average: number
  median: number
  unit?: string
}

export type FormManagementQuestionChartFileSummary = {
  totalFiles: number
  averageFilesPerAnswer: number
  totalSizeMb: number
  fileTypes: FormManagementQuestionChartSegment[]
  statuses: FormManagementQuestionChartSegment[]
}

export type FormManagementQuestionChartSuppression = {
  suppressed: boolean
  reason?: 'small_sample'
  threshold: number
  sampleSize: number
}

export type FormManagementQuestionChart = {
  id: string
  questionId: string
  questionTitle: string
  questionType: QuestionType
  typeLabel: string
  chartKind: FormManagementQuestionChartKind
  totalAnswers: number
  completionRate: number
  skipped: number
  invalid?: number
  summary: string
  segments: FormManagementQuestionChartSegment[]
  stacks?: FormManagementQuestionChartStack[]
  tokens?: FormManagementQuestionChartToken[]
  samples?: FormManagementQuestionChartSample[]
  numericSummary?: FormManagementQuestionChartNumericSummary
  fileSummary?: FormManagementQuestionChartFileSummary
  suppression?: FormManagementQuestionChartSuppression
}

export type FormManagementActivity = {
  id: string
  label: string
  description: string
  timeLabel: string
}

export type FormManagementProviderStatus = {
  id: string
  label: string
  state: 'configured' | 'not_configured' | 'mock_only'
  description: string
}

export type FormManagementExportFormatId = 'csv' | 'excel' | 'pdf' | 'json'

export type FormManagementExportFormat = {
  id: FormManagementExportFormatId
  label: string
  description: string
  enabled: boolean
}

export type FormManagementExportDetailColumn = {
  id: string
  label: string
  group: 'metadata' | 'answers' | 'hiddenFields' | 'customer'
  spreadsheetFormat?: 'text'
}

export type FormManagementExportDetailRow = {
  id: string
  recordNo: string
  responseId: string
  submittedAt: string
  statusLabel: string
  respondentLabel: string
  cells: Record<string, string>
}

export type FormManagementExportField = {
  id: string
  label: string
  group: 'metadata' | 'answers' | 'hiddenFields' | 'customer'
  selected: boolean
}

export type FormManagementExportFilter = {
  id: string
  label: string
  value: string
}

export type FormManagementExportJobPreview = {
  route: string
  method: 'GET' | 'POST'
  query: string
  estimatedRows: number
  permissionRequired: string
  providerState: FormManagementPanelState
}

export type FormManagementAnalysisQuestion = {
  id: string
  label: string
  typeLabel: string
  supported: boolean
  disabledReason?: string
}

export type FormManagementCrossAnalysisCell = {
  row: string
  column: string
  count: number
  percentage: number
}

export type FormManagementCrossAnalysis = {
  xQuestionId: string
  yQuestionId: string
  sampleSize: number
  rows: string[]
  columns: string[]
  cells: FormManagementCrossAnalysisCell[]
  insights: string[]
}

export type FormManagementVisitorSession = {
  id: string
  sourceLabel: string
  utmSource: string
  campaign: string
  device: string
  browser: string
  startedAt: string
  completedAt?: string
  status: 'completed' | 'started' | 'abandoned'
  hiddenFields: Array<{ label: string; value: string }>
}

export type FormManagementTrackingParameter = {
  id: string
  label: string
  value: string
  description: string
}

export type FormManagementGeneratedLink = {
  id: string
  name: string
  url: string
  count: number
  status: 'ready' | 'mock_only' | 'provider_not_configured'
  createdAt: string
}

export type FormManagementLinkGenerator = {
  templateColumns: string[]
  uploadState: FormManagementPanelState
  previewUrl: string
  parameters: FormManagementTrackingParameter[]
  generatedLinks: FormManagementGeneratedLink[]
}

export type FormManagementNotificationChannel = 'email' | 'manager_email' | 'webhook'

export type FormManagementNotificationRule = {
  id: string
  name: string
  channel: FormManagementNotificationChannel
  eventLabel: string
  recipients: string[]
  enabled: boolean
  providerState: FormManagementPanelState
  templateSubject: string
  templatePreview: string
}

export type FormManagementNotificationTestPreview = {
  route: string
  method: 'POST'
  payloadPreview: string
  providerState: FormManagementPanelState
}

export type FormManagementCompletionCta = {
  id: string
  label: string
  url: string
  style: 'primary' | 'secondary'
}

export type FormManagementCompletionPage = {
  title: string
  description: string
  animationStyle: 'check_confetti' | 'spotlight' | 'card_stack' | 'minimal'
  ctas: FormManagementCompletionCta[]
  redirectUrl?: string
  redirectDelaySeconds?: number
  allowResubmit: boolean
  showResubmitButton: boolean
  showShareButtons: boolean
  showNewSubmissionButton: boolean
  newSubmissionButtonLabel: string
}

export type FormManagementCustomerWorkflowFieldMapping = {
  id: string
  sourceQuestion: string
  targetField: string
  group:
    | 'workflow'
    | 'company'
    | 'contact'
    | 'payment'
    | 'address'
    | 'receiving'
    | 'sales'
    | 'classification'
    | 'region'
    | 'notes'
    | 'credit'
  required: boolean
}

export type FormManagementCustomerWorkflowReviewStep = {
  id: string
  label: string
  owner: string
  state: 'ready' | 'mock_only' | 'not_configured'
}

export type FormManagementCustomerWorkflow = {
  enabled: boolean
  requiresManagerReview: boolean
  autoCreateCustomer: boolean
  erpFilingStatus: 'not_filed' | 'filed' | 'not_configured'
  applicationsPending: number
  customersCreated: number
  fieldMappings: FormManagementCustomerWorkflowFieldMapping[]
  reviewSteps: FormManagementCustomerWorkflowReviewStep[]
}

export type FormManagementOperationAction = {
  id: 'duplicate' | 'move' | 'archive' | 'delete'
  label: string
  description: string
  enabled: boolean
  disabledReason?: string
  tone: 'default' | 'warning' | 'danger'
  confirmationTitle?: string
  confirmationDescription?: string
}

export type FormManagementWorkspaceData = {
  sourceMeta: ContractSourceMeta
  panelStates: Record<
    | 'analytics'
    | 'export'
    | 'crossAnalysis'
    | 'visitorActivity'
    | 'linkGenerator'
    | 'notifications'
    | 'completionPage'
    | 'customerWorkflow'
    | 'operations',
    FormManagementPanelState
  >
  metrics: FormManagementMetricContract[]
  trend: FormManagementTrendPoint[]
  dropoffRows: FormManagementDropoffRow[]
  questionStats: FormManagementQuestionStat[]
  questionCharts: FormManagementQuestionChart[]
  recentActivities: FormManagementActivity[]
  providerStatuses: FormManagementProviderStatus[]
  exportFormats: FormManagementExportFormat[]
  exportDetailColumns: FormManagementExportDetailColumn[]
  exportDetailRows: FormManagementExportDetailRow[]
  exportDetailPageSize: number
  exportFields: FormManagementExportField[]
  exportFilters: FormManagementExportFilter[]
  exportJobPreview: FormManagementExportJobPreview
  snapshotPreview: QuestionnaireSnapshotPreviewContract
  crossAnalysisQuestions: FormManagementAnalysisQuestion[]
  crossAnalysis: FormManagementCrossAnalysis
  visitorSessions: FormManagementVisitorSession[]
  linkGenerator: FormManagementLinkGenerator
  notificationRules: FormManagementNotificationRule[]
  notificationTestPreview: FormManagementNotificationTestPreview
  completionPage: FormManagementCompletionPage
  customerWorkflow: FormManagementCustomerWorkflow
  operationActions: FormManagementOperationAction[]
}

export const formManagementTabLabels: Record<FormManagementTabId, string> = {
  analytics: '圖表統計',
  export: '明細匯出',
  crossAnalysis: '交叉分析',
  visitorActivity: '訪客紀錄',
  linkGenerator: '網址產生器',
  notifications: '系統通知',
  completionPage: '完成頁',
  customerWorkflow: '建檔流程',
  operations: '操作設定',
}
